import { createSlice } from "@reduxjs/toolkit";
import dayjs from 'dayjs';

const initialState = {
    issueInvSearchParams: {
        primaryFilter: {
            requestBy: 1,
            requestByValue: '',
            requestStatusValue: '1,3',
        },
        moreFilter: {
            searchVehicleIds: '',
            searchJobCardIds: '',
            searchJobCardCategoryIds: '',
            searchInvItemIds: '',
            searchJobStatus: ''
        },
        showMoreFilter: false,
    },
    jobCardLists: {
        isLoading: false,
        dataLoaded: false,
        data: null,
        totalRecords: 0,
        pageNo: 0,
        perPageRecords: 10,
        jobCardError: '',
    },
    jobCardInventorySelectedItems: [],
    jobCardInventoryItemSerialNo: {
        serialNoForAction: 0,
        showSerialNoFormStatus: false,
        selectedItemForSerialNo: null,
        selectedItemIssuedQty: 0,
        serialNoValidating: false,
        selectedItemSerialNos: [],
        validatedSerialNos: [],
        invalidSerialNos: []
    },
    jobCardInventoryItemSubstitute: {
        showSubstituteItemModal: false,
        selectedItemForSubstitute: null,
        substituteItemListLoading: false,
        substituteItemLists: [],
        selectedSubstituteItems: [],
        similiarItemsError: ''
    },
    jobCardInventoryItemReturn: {
        selectedItemForReturn: null,
        issuedSerialNoLoading: false,
        issuedSerialNosDetails: [],
        issuedSerialNosLoadingError: '',
        updatingReturnItem: false,
    },
    loadJobCardList: false,
    showInventoryImport: false,
    updatingIssueInventory: false,
    loadingIssuedInfo: false,
}

const issueInventorySlice = createSlice({
    name: "issueInventory",
    initialState,
    reducers: {
        issueInvPrimaryFiltersUpdates: (state, { payload }) => {
            state.issueInvSearchParams.primaryFilter.requestBy = payload.requestBy;
            if (payload?.requestByValue !== '') {
                state.issueInvSearchParams.primaryFilter.requestByValue = payload.requestByValue;
            } else if (payload?.requestByValue === '') {
                state.issueInvSearchParams.primaryFilter.requestByValue = '';
            }
            if (payload?.requestStatusValue !== '') {
                state.issueInvSearchParams.primaryFilter.requestStatusValue = payload.requestStatusValue === undefined ? '1,3' : payload.requestStatusValue;
            } else if (payload?.requestStatusValue === '') {
                state.issueInvSearchParams.primaryFilter.requestStatusValue = '';
            }
        },

        issueInvMoreFiltersUpdates: (state, { payload }) => {
            if (payload?.requestBy) {
                state.issueInvSearchParams.primaryFilter.requestBy = payload.requestBy;
            }
            if (payload?.searchVehicleIds && payload?.searchVehicleIds !== '') {
                state.issueInvSearchParams.moreFilter.searchVehicleIds = payload.searchVehicleIds;
            } else if (payload?.searchVehicleIds === '') {
                state.issueInvSearchParams.moreFilter.searchVehicleIds = '';
            }

            if (payload?.searchJobCardIds && payload?.searchJobCardIds !== '') {
                state.issueInvSearchParams.moreFilter.searchJobCardIds = payload.searchJobCardIds;
            } else if (payload?.searchJobCardIds === '') {
                state.issueInvSearchParams.moreFilter.searchJobCardIds = '';
            }

            if (payload?.searchJobCardCategoryIds && payload?.searchJobCardCategoryIds !== '') {
                state.issueInvSearchParams.moreFilter.searchJobCardCategoryIds = payload.searchJobCardCategoryIds;
            } else if (payload?.searchJobCardCategoryIds === '') {
                state.issueInvSearchParams.moreFilter.searchJobCardCategoryIds = '';
            }

            if (payload?.searchInvItemIds && payload?.searchInvItemIds !== '') {
                state.issueInvSearchParams.moreFilter.searchInvItemIds = payload.searchInvItemIds;
            } else if (payload?.searchInvItemIds === '') {
                state.issueInvSearchParams.moreFilter.searchInvItemIds = '';
            }

            if (payload?.searchJobStatus && payload?.searchJobStatus !== '') {
                state.issueInvSearchParams.moreFilter.searchJobStatus = payload.searchJobStatus;
            } else if (payload?.searchJobStatus === '') {
                state.issueInvSearchParams.moreFilter.searchJobStatus = '';
            }
        },
        issueInvFilterToggle: (state, { payload }) => {
            state.issueInvSearchParams.showMoreFilter = payload
        },
        issueInvGetJobCardData: (state, { payload }) => {
            state.loadJobCardList = payload;
        },
        issueInvJobCardLoadingPending: (state) => {
            state.jobCardLists.isLoading = true;
            state.jobCardLists.dataLoaded = false;
        },
        issueInvJobCardLoadingSuccess: (state, { payload }) => {
            state.jobCardLists.isLoading = false;
            state.jobCardLists.data = payload;
            state.jobCardLists.jobCardError = '';
            state.jobCardLists.dataLoaded = true;
            state.loadJobCardList = false;
        },
        issueInvJobCardLoadingFailed: (state, { payload }) => {
            state.jobCardLists.isLoading = false;
            state.jobCardLists.data = []
            state.jobCardLists.jobCardError = payload;
            state.jobCardLists.dataLoaded = true;
            state.loadJobCardList = false;
        },
        issueInvJobCardTotalRecordsUpdate: (state, { payload }) => {
            state.jobCardLists.totalRecords = payload || 0;
        },
        issueInvJobCardPageNoUpdate: (state, { payload }) => {
            state.jobCardLists.pageNo = payload;
            state.loadJobCardList = true;
        },
        issueInvJobCardPerPageRecordsUpdate: (state, { payload }) => {
            state.jobCardLists.perPageRecords = payload;
            state.jobCardLists.pageNo = 0;
            state.loadJobCardList = true;
        },
        showSerialNoForm: (state, { payload }) => {
            state.jobCardInventoryItemSerialNo.showSerialNoFormStatus = true;
            state.jobCardInventoryItemSerialNo.serialNoForAction = payload.serialNoForAction;
            state.jobCardInventoryItemSerialNo.selectedItemForSerialNo = payload.selectedItem;
            state.jobCardInventoryItemSerialNo.selectedItemIssuedQty = payload.issuedQty;
        },
        hideSerialNoForm: (state, { payload }) => {
            state.jobCardInventoryItemSerialNo.showSerialNoFormStatus = false;
            state.jobCardInventoryItemSerialNo.serialNoForAction = 0;
            state.jobCardInventoryItemSerialNo.selectedItemForSerialNo = null;
            state.jobCardInventoryItemSerialNo.selectedItemIssuedQty = 0;
        },
        createSerialNoInputs: (state, { payload }) => {
            state.jobCardInventoryItemSerialNo.selectedItemSerialNos = payload;
        },
        updateSerialNoInputValue: (state, { payload }) => {
            if (payload.index >= 0) {
                state.jobCardInventoryItemSerialNo.selectedItemSerialNos[payload?.index].serialNo = payload?.value || '';
            }
        },
        clearSerialNo: (state) => {
            state.jobCardInventoryItemSerialNo.selectedItemSerialNos = [];
            state.jobCardInventoryItemSerialNo.validatedSerialNos = [];
            state.jobCardInventoryItemSerialNo.invalidSerialNos = [];
        },

        serialNoValidationPending: (state) => {
            state.jobCardInventoryItemSerialNo.serialNoValidating = true;
        },
        serialNoValidationSuccess: (state, { payload }) => {
            state.jobCardInventoryItemSerialNo.serialNoValidating = false;
            state.jobCardInventoryItemSerialNo.validatedSerialNos = payload.validSrNos;
            state.jobCardInventoryItemSerialNo.invalidSerialNos = payload.invalidSrNos;
        },
        serialNoValidationFailed: (state, { payload }) => {
            state.jobCardInventoryItemSerialNo.serialNoValidating = false;
            state.jobCardInventoryItemSerialNo.validatedSerialNos = [];
            state.jobCardInventoryItemSerialNo.invalidSerialNos = [];
        },
        issueInvImportToggle: (state, { payload }) => {
            state.showInventoryImport = payload
        },
        selectJobCardInventoryItem: (state, { payload }) => {
            const selectedRowIndex = state.jobCardInventorySelectedItems.findIndex((item) => item.jobCardInvId === payload.selectedItemRow.jobCardInvId);
            if (selectedRowIndex >= 0) {
                state.jobCardInventorySelectedItems[selectedRowIndex] = payload.selectedItemRow;
            } else {
                state.jobCardInventorySelectedItems.push(payload.selectedItemRow);
            }
        },
        deSelectJobCardInventoryItem: (state, { payload }) => {
            const selectedRowIndex = state.jobCardInventorySelectedItems.findIndex((item) => item.jobCardInvId === payload.jobCardInvId);
            if (selectedRowIndex >= 0) {
                state.jobCardInventorySelectedItems.splice(selectedRowIndex, 1);
            }
        },
        deSelectAllJobCardInventoryItem: (state, { payload }) => {
            state.jobCardInventorySelectedItems = [];
        },
        deSelectAllJobCardInventoryItemsForJobCard: (state, { payload }) => {
            state.jobCardInventorySelectedItems = state.jobCardInventorySelectedItems.filter((i) => i.jobCardId !== payload.jobCardId);
        },
        updateIssueQtyForJobCardInventoryItem: (state, { payload }) => {
            const selectedRowIndex = state.jobCardInventorySelectedItems.findIndex((item) => item.jobCardInvId === payload.jobCardInvId);
            if (selectedRowIndex >= 0) {
                state.jobCardInventorySelectedItems[selectedRowIndex].issueQty = payload.issueQty
            }
        },
        updateIssueToForJobCardInventoryItem: (state, { payload }) => {
            const selectedRowIndex = state.jobCardInventorySelectedItems.findIndex((item) => item.jobCardInvId === payload.jobCardInvId);
            if (selectedRowIndex >= 0) {
                state.jobCardInventorySelectedItems[selectedRowIndex].issueTo = payload.issueTo
            }
        },
        updateIssueDescriptionForJobCardInventoryItem: (state, { payload }) => {
            const selectedRowIndex = state.jobCardInventorySelectedItems.findIndex((item) => item.jobCardInvId === payload.jobCardInvId);
            if (selectedRowIndex >= 0) {
                state.jobCardInventorySelectedItems[selectedRowIndex].issueDescription = payload.issueDescription
            }
        },
        updateIssueSerialNosForJobCardInventoryItem: (state, { payload }) => {
            const selectedRowIndex = state.jobCardInventorySelectedItems.findIndex((item) => item.jobCardInvId === payload.jobCardInvId);
            if (selectedRowIndex >= 0) {
                state.jobCardInventorySelectedItems[selectedRowIndex].serialNos = payload.serialNos;
                state.jobCardInventoryItemSerialNo.selectedItemSerialNos = [];
                state.jobCardInventoryItemSerialNo.validatedSerialNos = [];
                state.jobCardInventoryItemSerialNo.invalidSerialNos = [];
            }
        },
        updateIssuedTimeForJobCardInventoryItem: (state, { payload }) => {
            const selectedRowIndex = state.jobCardInventorySelectedItems.findIndex((item) => item.jobCardInvId === payload.jobCardInvId);
            if (selectedRowIndex >= 0) {
                state.jobCardInventorySelectedItems[selectedRowIndex].issuedTime = dayjs(payload.issuedTime).format("YYYY-MM-DD HH:mm:ss");
            }
        },
        issueInventoryUpdatesPending: (state) => {
            state.updatingIssueInventory = true;
        },
        issueInventoryUpdatesSuccess: (state, { payload }) => {
            state.updatingIssueInventory = false;
        },
        issueInventoryUpdatesFailed: (state, { payload }) => {
            state.updatingIssueInventory = false;
        },
        // Substitute Item Events
        showSubstituteItemModal: (state, { payload }) => {
            state.jobCardInventoryItemSubstitute.showSubstituteItemModal = true;
            state.jobCardInventoryItemSubstitute.selectedItemForSubstitute = payload.selectedItem;
        },
        hideSubstituteItemModal: (state, { payload }) => {
            state.jobCardInventoryItemSubstitute.showSubstituteItemModal = false;
            state.jobCardInventoryItemSubstitute.selectedItemForSubstitute = null;
            state.jobCardInventoryItemSubstitute.substituteItemListLoading = false;
            state.jobCardInventoryItemSubstitute.substituteItemLists = [];
            state.jobCardInventoryItemSubstitute.selectedSubstituteItems = [];
        },
        substitueSimiliarItemsPending: (state) => {
            state.jobCardInventoryItemSubstitute.substituteItemListLoading = true;
        },
        substitueSimiliarItemsSuccess: (state, { payload }) => {
            state.jobCardInventoryItemSubstitute.substituteItemListLoading = false;
            state.jobCardInventoryItemSubstitute.substituteItemLists = payload.similiarItems;
            state.jobCardInventoryItemSubstitute.selectedSubstituteItems = [];
            state.jobCardInventoryItemSubstitute.similiarItemsError = '';
        },
        substitueSimiliarItemsFailed: (state, { payload }) => {
            state.jobCardInventoryItemSubstitute.substituteItemListLoading = false;
            state.jobCardInventoryItemSubstitute.substituteItemLists = [];
            state.jobCardInventoryItemSubstitute.selectedSubstituteItems = [];
            state.jobCardInventoryItemSubstitute.similiarItemsError = payload;
        },
        selectSubstituteItem: (state, { payload }) => {
            const selectedRowIndex = state.jobCardInventoryItemSubstitute.selectedSubstituteItems.findIndex((item) => item.jobCardInvId === payload.selectedItemRow.jobCardInvId && item.itemId === payload.selectedItemRow.itemId);
            if (selectedRowIndex >= 0) {
                state.jobCardInventoryItemSubstitute.selectedSubstituteItems[selectedRowIndex] = payload.selectedItemRow;
            } else {
                state.jobCardInventoryItemSubstitute.selectedSubstituteItems.push(payload.selectedItemRow);
            }
        },
        deSelectSubstituteItem: (state, { payload }) => {
            const selectedRowIndex = state.jobCardInventoryItemSubstitute.selectedSubstituteItems.findIndex((item) => item.jobCardInvId === payload.selectedItemRow.jobCardInvId && item.itemId === payload.selectedItemRow.itemId);
            if (selectedRowIndex >= 0) {
                state.jobCardInventoryItemSubstitute.selectedSubstituteItems.splice(selectedRowIndex, 1);
            }
        },
        deSelectAllSubstituteItem: (state) => {
            state.jobCardInventoryItemSubstitute.selectedSubstituteItems = [];
        },
        updateIssueQtyForSubstituteItem: (state, { payload }) => {
            const selectedRowIndex = state.jobCardInventoryItemSubstitute.selectedSubstituteItems.findIndex((item) => item.jobCardInvId === payload.jobCardInvId && item.itemId === payload.itemId);
            if (selectedRowIndex >= 0) {
                state.jobCardInventoryItemSubstitute.selectedSubstituteItems[selectedRowIndex].issueQty = payload.issueQty
            }
        },
        updateIssueSerialNosForSubstituteItem: (state, { payload }) => {
            const selectedRowIndex = state.jobCardInventoryItemSubstitute.selectedSubstituteItems.findIndex((item) => item.jobCardInvId === payload.jobCardInvId && item.itemId === payload.itemId);
            if (selectedRowIndex >= 0) {
                state.jobCardInventoryItemSubstitute.selectedSubstituteItems[selectedRowIndex].serialNos = payload.serialNos;
                state.jobCardInventoryItemSerialNo.selectedItemSerialNos = [];
                state.jobCardInventoryItemSerialNo.validatedSerialNos = [];
                state.jobCardInventoryItemSerialNo.invalidSerialNos = [];
            }
        },
        // Return Item Events
        setSelectedReturnItem: (state, { payload }) => {
            state.jobCardInventoryItemReturn.selectedItemForReturn = payload.selectedItem;
        },
        hideReturnItemModal: (state, { payload }) => {
            state.jobCardInventoryItemReturn.selectedItemForReturn = null;
            state.jobCardInventoryItemReturn.itemSerialNoLoading = false;
        },
        updateReturnQtyForReturnItem: (state, { payload }) => {
            if (typeof payload.returnQty === 'number') {
                state.jobCardInventoryItemReturn.selectedItemForReturn.returnQty = payload.returnQty;
            }
        },
        updateReplaceQtyForReturnItem: (state, { payload }) => {
            if (typeof payload.replaceQty === 'number') {
                state.jobCardInventoryItemReturn.selectedItemForReturn.replaceQty = payload.replaceQty;
            }
        },
        updateExcessQtyForReturnItem: (state, { payload }) => {
            if (typeof payload.excessQty === 'number') {
                state.jobCardInventoryItemReturn.selectedItemForReturn.excessQty = payload.excessQty;
            }
        },
        updateDefectiveQtyForReturnItem: (state, { payload }) => {
            if (typeof payload.defectiveQty === 'number') {
                state.jobCardInventoryItemReturn.selectedItemForReturn.defectiveQty = payload.defectiveQty;
            }
        },
        updateScrapCheckDefectiveQtyForReturnItem: (state, { payload }) => {
            if (typeof payload.scrapDefective === 'string') {
                state.jobCardInventoryItemReturn.selectedItemForReturn.scrapDefective = payload.scrapDefective;
            }
        },
        updateRemarkForReturnItem: (state, { payload }) => {
            if (typeof payload.returnRemark === 'string') {
                state.jobCardInventoryItemReturn.selectedItemForReturn.returnRemark = payload.returnRemark;
            }
        },
        issuedSerialNoLoadingPending: (state) => {
            state.jobCardInventoryItemReturn.issuedSerialNoLoading = true;
            state.jobCardInventoryItemReturn.issuedSerialNosLoadingError = '';
        },
        issuedSerialNoLoadingSuccess: (state, { payload }) => {
            state.jobCardInventoryItemReturn.issuedSerialNoLoading = false;
            state.jobCardInventoryItemReturn.issuedSerialNosDetails = payload.issuedSrNosDetails;
            state.jobCardInventoryItemReturn.issuedSerialNosLoadingError = '';
            let srNoRowsForSelectedItem = [];
            // Add The Serial No details in selectedItem SrNo Array
            for (let srNoRec of payload.issuedSrNosDetails) {
                let srNoRow = { ...srNoRec, isActive: false, isExcess: 'N', isDefective: 'N', isDefectiveItemScrap: 'N', replacedSerialNo: '', returnRemark: '' }
                srNoRowsForSelectedItem.push(srNoRow);
            }
            state.jobCardInventoryItemReturn.selectedItemForReturn.serialNos = srNoRowsForSelectedItem;
        },
        issuedSerialNoLoadingFailed: (state, { payload }) => {
            state.jobCardInventoryItemReturn.issuedSerialNoLoading = false;
            state.jobCardInventoryItemReturn.issuedSerialNosDetails = [];
            state.jobCardInventoryItemReturn.issuedSerialNosLoadingError = payload;
        },
        updateIssuedSerialNoReturnInputs: (state, { payload }) => {
            if (typeof payload.index === 'number') {
                if (state.jobCardInventoryItemReturn.selectedItemForReturn.serialNos[payload.index]) {
                    if (payload.updateFor === 'rowSelection') {
                        state.jobCardInventoryItemReturn.selectedItemForReturn.serialNos[payload.index].isActive = payload.updatedValue;
                    }
                    if (payload.updateFor === 'excessItem') {
                        state.jobCardInventoryItemReturn.selectedItemForReturn.serialNos[payload.index].isExcess = payload.updatedValue;
                    }
                    if (payload.updateFor === 'defectiveItem') {
                        state.jobCardInventoryItemReturn.selectedItemForReturn.serialNos[payload.index].isDefective = payload.updatedValue;
                    }
                    if (payload.updateFor === 'defectiveItemScrap') {
                        state.jobCardInventoryItemReturn.selectedItemForReturn.serialNos[payload.index].isDefectiveItemScrap = payload.updatedValue;
                    }
                    if (payload.updateFor === 'replacedSerialNo') {
                        state.jobCardInventoryItemReturn.selectedItemForReturn.serialNos[payload.index].replacedSerialNo = payload.updatedValue;
                    }
                    if (payload.updateFor === 'returnRemark') {
                        state.jobCardInventoryItemReturn.selectedItemForReturn.serialNos[payload.index].returnRemark = payload.updatedValue;
                    }
                }
            }
        },
        resetSelectedItemReturnDetails: (state) => {
            if (state.jobCardInventoryItemReturn.selectedItemForReturn.serialNoApp === 'N') {
                state.jobCardInventoryItemReturn.selectedItemForReturn = { ...state.jobCardInventoryItemReturn.selectedItemForReturn, returnQty: 0, replaceQty: 0, excessQty: 0, defectiveQty: 0, scrapDefective: 'N', returnRemark: '', serialNos: [] };
            } else {
                state.jobCardInventoryItemReturn.selectedItemForReturn = { ...state.jobCardInventoryItemReturn.selectedItemForReturn, returnQty: 0, replaceQty: 0, excessQty: 0, defectiveQty: 0, scrapDefective: 'N', returnRemark: '' };
                state.jobCardInventoryItemReturn.selectedItemForReturn.serialNos = state.jobCardInventoryItemReturn.selectedItemForReturn.serialNos.map((srNoRow) => {
                    return { ...srNoRow, isActive: false, isExcess: 'N', isDefective: 'N', isDefectiveItemScrap: 'N', replacedSerialNo: '', returnRemark: '' };
                })
            }
        },
        issueInventoryReturnUpdatesPending: (state) => {
            state.jobCardInventoryItemReturn.updatingReturnItem = true;
        },
        issueInventoryReturnUpdatesSuccess: (state, { payload }) => {
            state.jobCardInventoryItemReturn.updatingReturnItem = false;
        },
        issueInventoryReturnUpdatesFailed: (state, { payload }) => {
            state.jobCardInventoryItemReturn.updatingReturnItem = false;
        },
        loadingIssuedInfoPending: (state) => {
            state.loadingIssuedInfo = true;
        },
        loadingIssuedInfoSuccess: (state, { payload }) => {
            state.loadingIssuedInfo = false;
        },
        loadingIssuedInfoFailed: (state, { payload }) => {
            state.loadingIssuedInfo = false;
        },
    }
});


const { reducer, actions } = issueInventorySlice;

export const {
    issueInvPrimaryFiltersUpdates, issueInvMoreFiltersUpdates, issueInvFilterToggle, issueInvGetJobCardData,
    issueInvJobCardLoadingPending, issueInvJobCardLoadingSuccess, issueInvJobCardLoadingFailed,
    issueInvJobCardTotalRecordsUpdate, issueInvJobCardPageNoUpdate, issueInvJobCardPerPageRecordsUpdate,
    showSerialNoForm, hideSerialNoForm, createSerialNoInputs, updateSerialNoInputValue, clearSerialNo, issueInvImportToggle,
    serialNoValidationPending, serialNoValidationSuccess, serialNoValidationFailed,
    selectJobCardInventoryItem, deSelectJobCardInventoryItem, deSelectAllJobCardInventoryItem, deSelectAllJobCardInventoryItemsForJobCard,
    updateIssueQtyForJobCardInventoryItem, updateIssueToForJobCardInventoryItem,
    updateIssueDescriptionForJobCardInventoryItem, updateIssueSerialNosForJobCardInventoryItem, updateIssuedTimeForJobCardInventoryItem,
    issueInventoryUpdatesPending, issueInventoryUpdatesSuccess, issueInventoryUpdatesFailed,
    showSubstituteItemModal, hideSubstituteItemModal,
    substitueSimiliarItemsPending, substitueSimiliarItemsSuccess, substitueSimiliarItemsFailed,
    selectSubstituteItem, deSelectSubstituteItem, deSelectAllSubstituteItem, updateIssueQtyForSubstituteItem,
    updateIssueSerialNosForSubstituteItem, setSelectedReturnItem, hideReturnItemModal,
    updateReturnQtyForReturnItem, updateReplaceQtyForReturnItem, updateExcessQtyForReturnItem, updateDefectiveQtyForReturnItem,
    updateScrapCheckDefectiveQtyForReturnItem, updateRemarkForReturnItem,
    issuedSerialNoLoadingPending, issuedSerialNoLoadingSuccess, issuedSerialNoLoadingFailed,
    updateIssuedSerialNoReturnInputs, resetSelectedItemReturnDetails,
    issueInventoryReturnUpdatesPending, issueInventoryReturnUpdatesSuccess, issueInventoryReturnUpdatesFailed,
    loadingIssuedInfoPending, loadingIssuedInfoSuccess, loadingIssuedInfoFailed
} = actions;

export default reducer;


